// ── theme sentiment chips (QA #10) ──────────────────────────────────────────
// The backend codes every clustered theme with `sentiment ∈ {positive, negative,
// mixed, neutral}`. It renders as a quiet, TEXT-ONLY chip on the THEME LABEL — never
// on the series bars (series color = stimulus identity). No icon/glyph: the word IS
// the signal (never color-alone, so it stays meaningful for colorblind/screen-reader
// users), with a faint semantic tint as a secondary cue only. Deliberately one font
// size below the theme label and visually quiet (soft tint / hairline border, no
// outline) so it never competes with the label — "Neutral" is the quietest of all,
// carrying no color tint.
//
// LEAF module (imports only `cn`): both `QuickStudyOpenEndCard` (which re-exports for
// its existing importers) and `MonadicThemeHeatmap` consume these — housing them here
// keeps that pair cycle-free, mirroring the `openEndThemeGroups` precedent.
import { cn } from '@/lib/utils'

const SENTIMENT_CHIP: Record<string, { label: string; className: string }> = {
  positive: { label: 'Positive', className: 'bg-chart-positive/10 text-chart-positive' },
  negative: { label: 'Negative', className: 'bg-destructive/10 text-destructive' },
  mixed: { label: 'Mixed', className: 'bg-chart-warning/10 text-chart-warning' },
  neutral: { label: 'Neutral', className: 'border border-twin-chat-divider text-twin-chat-fg-muted' },
}

export function SentimentChip({ sentiment, label }: { sentiment?: string; label?: string }) {
  const key = String(sentiment ?? '').trim().toLowerCase()
  const def = SENTIMENT_CHIP[key]
  if (!def) return null
  const { label: defaultLabel, className } = def
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium leading-4',
        className,
      )}
      data-testid="qs-theme-sentiment"
      data-sentiment={key}
    >
      {label ?? defaultLabel}
    </span>
  )
}

/** Group-header variant: the middle section absorbs mixed AND neutral (and unknown)
 *  sentiments, so its header says "Mixed / neutral" — a per-theme chip stays plain "Mixed". */
export function SentimentGroupChip({ group }: { group: 'positive' | 'mixed' | 'negative' }) {
  return <SentimentChip sentiment={group} label={group === 'mixed' ? 'Mixed / neutral' : undefined} />
}
